import { useQuery } from '@tanstack/react-query';
import { ClassService } from '@/services/classService';
import { ScheduleService } from '@/services/classServices/scheduleService';
import { Class, ClassSchedule } from '@/types/classTypes';

export const useScheduleData = (organizationId: string, classId: string | null) => {
  // Fetch classes
  const { 
    data: classes = [], 
    isLoading: classesLoading,
    error: classesError
  } = useQuery({
    queryKey: ['classes', organizationId],
    queryFn: () => ClassService.getClasses(organizationId),
  });

  // Helper function to convert DB schedule to ClassSchedule interface
  const normalizeSchedule = (schedule: any): ClassSchedule => {
    // Ensure weekday is properly normalized
    let weekday = schedule.weekday;
    
    // Handle day_of_week if it exists and weekday is undefined
    if (weekday === undefined && schedule.day_of_week !== undefined) {
      weekday = typeof schedule.day_of_week === 'string' 
        ? parseInt(schedule.day_of_week, 10) 
        : schedule.day_of_week;
    }
    
    return {
      ...schedule,
      weekday,
      day_of_week: weekday !== undefined ? String(weekday) : undefined
    };
  };
  
  // Fetch schedules more efficiently - one query instead of multiple
  const {
    data: rawSchedules = [],
    isLoading: schedulesLoading,
    error: schedulesError,
    refetch: refetchSchedules
  } = useQuery({
    queryKey: ['schedules', organizationId, classId],
    queryFn: async () => {
      if (classId) {
        // If we have a specific class ID, just fetch schedules for that class
        return await ScheduleService.getSchedulesForClass(classId);
      } else {
        // For all classes, make a single batch request instead of individual requests
        return await ScheduleService.getAllSchedulesForOrganization(organizationId);
      }
    },
    enabled: !classesLoading && organizationId !== '',
  });

  // Normalize all schedules to have consistent shape
  const schedules: ClassSchedule[] = rawSchedules.map(normalizeSchedule);

  // Add class schedules to classes for easy lookup (UI only, not persisted in DB)
  const enhancedClasses = classes.map(cls => {
    const classSchedules = schedules.filter(schedule => schedule.class_id === cls.id);
    return {
      ...cls,
      schedules: classSchedules // UI only, not persisted in DB
    };
  });

  return {
    classes,
    schedules,
    enhancedClasses,
    isLoading: classesLoading || schedulesLoading,
    error: classesError || schedulesError,
    refetchSchedules
  };
};
